'use client';

import { useEffect, useState, FC, useContext } from 'react';
import {
  Button,
  ButtonGroup,
  Flex,
  Input,
  Select,
  InputChips,
  EntityTypeSelector,
  Separator,
} from '@/_components';
import type { DetailsFormProps } from './DetailsForm.types';
import { updateCompany, UpdateCompanyAttrs } from '@/_api/admin-companies';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { COUNTRY_ITEMS } from '@/_constants/countries';
import { ENTITY_TYPES } from '@/_constants/entities';
import { Company } from '@/_types/models/company';
import { updateEntityTags } from '@/_api/admin-tags';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { EntityV2Context } from '@/_contexts/admin/EntityV2Provider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { countries } from 'countries-list';

const DEFAULT_ENTITY_TYPE: string = ENTITY_TYPES[0];

export const DetailsForm: FC<DetailsFormProps> = ({ company, onClose }) => {
  const { selectedCompanyTags, updateSelectedCompany, refreshCompanyDetails } =
    useContext(CompanyContext);
  const { updateSelectedEntity } = useContext(EntityV2Context);
  const { token } = useContext(UserContext);
  const { entityTagOptionsToAdd, preferences } =
    useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);
  const [customEntityType, setCustomEntityType] = useState<string>('');

  const [isLoading, setLoading] = useState<boolean>(false);
  const [entityType, setEntityType] = useState<string>(DEFAULT_ENTITY_TYPE);

  const onEntityTypeChange = (newEntityType: string) =>
    setEntityType(newEntityType);

  const [entityName, setEntityName] = useState<string>('');
  const [businessEmail, setBusinessEmail] = useState<string>('');
  const [otherEmail, setOtherEmail] = useState<string>('');
  const [externalEntityId, setExternalEntityId] = useState<string>('');
  const [taxReturnId, setTaxReturnId] = useState<string>('');

  const [chips, setChips] = useState<Set<string>>(new Set(selectedCompanyTags));
  const [chipText, setChipText] = useState<string>('');

  const onChipText = (event: React.ChangeEvent<HTMLInputElement>) =>
    setChipText(event.target.value);

  const onEntityNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEntityName(event.target.value);
  const onBusinessEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setBusinessEmail(event.target.value);
  const onOtherEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setOtherEmail(event.target.value);
  const onExternalEntityId = (event: React.ChangeEvent<HTMLInputElement>) =>
    setExternalEntityId(event.target.value);
  const onTaxReturnIdChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setTaxReturnId(event.target.value);

  const [streetAddress, setStreetAddress] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [zip, setZip] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [country, setCountry] = useState<string>('');

  const onStreetAddressChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setStreetAddress(event.target.value);
  const onCityChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCity(event.target.value);
  const onZipChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setZip(event.target.value);
  const onStateChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setState(event.target.value);
  const onCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setCountry(event.target.value);

  const onSave = async () => {
    try {
      setLoading(true);

      if (entityName.includes('/')) {
        return addToast('error', 'Entity Name cannot contain a "/" character.');
      }

      if (entityName.includes('"')) {
        return addToast('error', 'Entity Name cannot contain a " character.');
      }

      const updatedCompany: Partial<UpdateCompanyAttrs> = {
        entity_type: customEntityType || entityType,
        name: entityName,
        business_email: businessEmail,
        external_entity_id: externalEntityId,
        other_email: otherEmail,
        street: streetAddress || null,
        country: country || null,
        city: city || null,
        zip: zip || null,
        state: state || null,
        tax_return_id: taxReturnId || null,
      };

      await updateEntityTags({
        company_id: company.id,
        entity_tags: Array.from(chips),
        token,
      });

      const refreshedCompany = await updateCompany({
        ...(updatedCompany as Company),
        token,
        companyId: company?.id!,
        userId: company?.user?.id!,
      });

      if (!refreshedCompany?.error) {
        addToast('success', 'Entity info updated successfully.');
        refreshCompanyDetails();
        updateSelectedCompany(refreshedCompany);
        updateSelectedEntity(refreshedCompany);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    if (company?.id) {
      setEntityType(company.entity_type);
      setCustomEntityType('');
      setEntityName(company.name);
      setBusinessEmail(company.business_email || '');
      setOtherEmail(company.other_email || '');
      setStreetAddress(company.street || '');
      setCountry(company.country || '');
      setCity(company.city || '');
      setZip(company.zip || '');
      setState(company.state || '');
      setExternalEntityId(company.external_entity_id || '');
      setChips(new Set(selectedCompanyTags));
      setTaxReturnId(company.tax_return_id || '');
    }
  }, [company, selectedCompanyTags]);

  return (
    <Flex isColumn={true} gap={10}>
      {/* Entity Type - Entity Name - Business Email */}
      <Flex gap={6}>
        <Input
          label="Entity Name"
          placeholder="Entity Name"
          name="company-name"
          isDisabled={isLoading}
          value={entityName}
          isRequired={true}
          onChange={onEntityNameChange}
        />
        <EntityTypeSelector onChange={onEntityTypeChange} value={entityType} />
        {preferences?.review_and_export_enabled && (
          <Input
            label="Tax Software Return ID"
            value={taxReturnId}
            isDisabled={isLoading}
            placeholder="1234"
            onChange={onTaxReturnIdChange}
          />
        )}
      </Flex>
      <Flex isColumn={true} gap={4}>
        <Flex gap={6}>
          <Input
            label="Entity Email"
            placeholder="hello@example.com"
            value={businessEmail}
            isDisabled={isLoading}
            onChange={onBusinessEmailChange}
          />
          <Input
            label="Other Email"
            value={otherEmail}
            placeholder="other@example.com"
            isDisabled={isLoading}
            onChange={onOtherEmailChange}
          />
          {preferences?.external_entity_id_allowed && (
            <>
              <Input
                label="External Entity ID"
                value={externalEntityId}
                placeholder="JSMITH123"
                isDisabled={isLoading}
                onChange={onExternalEntityId}
              />
            </>
          )}
        </Flex>
      </Flex>
      <Flex gap={6}>
        <Input
          label="Street Address"
          placeholder="e.g., Oak Street"
          value={streetAddress}
          isDisabled={isLoading}
          onChange={onStreetAddressChange}
        />
        <Select
          items={COUNTRY_ITEMS}
          label="Country"
          onChange={onCountryChange}
          isDisabled={isLoading}
          selectedValue={country in countries ? country : ''}
        />
      </Flex>
      <Flex gap={6}>
        <Input
          label="City"
          placeholder="e.g., San Francisco"
          value={city}
          isDisabled={isLoading}
          onChange={onCityChange}
        />
        <Input
          label={country === 'CA' ? 'Province/Territory' : 'State'}
          placeholder={country === 'CA' ? 'e.g., BC' : 'e.g., CA'}
          value={state}
          isDisabled={isLoading}
          onChange={onStateChange}
        />
        <Input
          label={country === 'CA' ? 'Postal Code' : 'Zip Code'}
          placeholder={country === 'CA' ? 'e.g., V5T 1B3' : 'e.g., 94120'}
          value={zip}
          isDisabled={isLoading}
          onChange={onZipChange}
        />
      </Flex>
      <Flex gap={6}>
        <InputChips
          results={entityTagOptionsToAdd}
          chips={chips}
          label="Entity Tag"
          isDisabled={isLoading || !entityTagOptionsToAdd?.length}
          onChange={onChipText}
          setChips={setChips}
          value={chipText}
          placeholder="Select Tag"
        />
      </Flex>
      <Separator isLight={true} />
      <ButtonGroup align="right">
        <Button
          intent="secondary"
          value="Cancel"
          onClick={onClose}
          isDisabled={isLoading}
        />
        <Button
          intent="primary"
          value="Update"
          onClick={onSave}
          isDisabled={isLoading}
          isLoading={isLoading}
        />
      </ButtonGroup>
    </Flex>
  );
};
